import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';

export type DataTypes =
  | "String"
  | "Integer"
  | "Decimal"
  | "Boolean"
  | "DateTime";

export interface SystemSettingsDTO {
    id: string;
    title: string;
    key: string;
    value: string;
    dataType: DataTypes;
    description?: string;
    isActive: boolean;

    // Nuevos campos multi-tenant
    isOverride: boolean;    // true si es override del tenant
    defaultValue?: string;  // valor global original (si isOverride=true)
    canReset: boolean;      // true si puede resetear a default

    // Campo adicional para manejo en UI (agregado durante parsing)
    valueBoolean?: boolean; // valor convertido a boolean para uso en toggle
}

@Injectable({
    providedIn: 'root'
})
export class SystemSettingsService {
    private apiUrl = `${environment.apiUrl}/system-configuration`;
    private platformApiUrl = `${environment.apiUrl}/platform/system-configuration`;

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) { }

    getAll(): Observable<any> {
        return this.http.get(this.apiUrl);
    }

    // Actualiza una configuración (crea/actualiza override del tenant o global para Platform Admin)
    update(key: string, value: boolean): Observable<SystemSettingsDTO> {
        // Si es Platform Admin, usar endpoint de plataforma
        const url = this.authService.isPlatformAdmin()
            ? `${this.platformApiUrl}/${key}`
            : `${this.apiUrl}/${key}`;

        return this.http.put<SystemSettingsDTO>(url, { value })
            .pipe(
                catchError(this.handleError)
            );
    }

    // Resetea una configuración al valor global (elimina override del tenant)
    resetToDefault(key: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${key}/reset`)
            .pipe(
                catchError(this.handleError)
            );
    }

    // Obtiene todas las configuraciones (opcional, si luego lo necesitas)
    getSettings(): Observable<any> {
        return this.http.get(this.apiUrl);
    }

    private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error desconocido';
    
    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      if (error.status === 0) {
        errorMessage = 'No se pudo conectar con el servidor. Por favor, verifica tu conexión.';
      } else if (error.status === 404) {
        errorMessage = 'No se encontró el recurso solicitado.';
      } else if (error.status === 400) {
        if (error.error && Array.isArray(error.error)) {
          errorMessage = error.error.map(err => err.errorMessage).join(', ');
        } else {
          errorMessage = 'Solicitud incorrecta. Verifica los datos enviados.';
        }
      } else if (error.status === 401) {
        errorMessage = 'No estás autorizado para realizar esta acción.';
      } else if (error.status === 403) {
        errorMessage = 'No tienes permisos para acceder a este recurso.';
      } else {
        errorMessage = `Código de error: ${error.status}, mensaje: ${error.message}`;
      }
    }
    
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}